import { useEffect, useReducer, useState } from 'react';
import { connect } from 'react-redux';

import timedFetch from 'middleware/timedFetch';
import { fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import { trackError } from 'helpers/ErrorUtils';
import { formInputsToObject } from 'helpers/HtmlHelpers';
import { AriaLiveTee } from 'components/common/AriaLive';
import { AD_PREFERENCE_COOKIE } from 'constants/cookies';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { fetchAdPreferences, setAdPreferences } from 'actions/account/ads';

import css from 'styles/components/landing/zapposForm.scss';

export const FormElement = ({ tagName, type, value, label, name, required, setPrefValue, optOutPreference, index }) => {
  switch (tagName) {
    case 'input':
      return (
        <p className={css.option} key={`${name}-${index}-${optOutPreference}`}>
          <input
            type={type}
            required={required === 'true' || required === true}
            name={name}
            id={`${name}-${index}`}
            onChange={setPrefValue.bind(this, value)}
            value={value}
            defaultChecked={value === optOutPreference?.toString()}
          />
          <label htmlFor={`${name}-${index}`}>{label}</label>
        </p>
      );
    case 'button':
      /* take type as a property https://github.com/yannickcr/eslint-plugin-react/issues/1846 */
      /* eslint-disable react/button-has-type */
      return (
        <button
          key={`${value}-${index}`}
          className={css.submit}
          type={type}
          value={value}>
          {label}
        </button>
      );
      /* eslint-enable */
  }
};

const initialState = {
  error: false,
  success: false
};

const ERROR = 'ERROR';
const SUCCESS = 'SUCCESS';
const RESET = 'RESET';
function reducer(state, action) {
  switch (action) {
    case ERROR:
      return { error: true, success: false };
    case SUCCESS:
      return { error: false, success: true };
    case RESET:
      return initialState;
    default:
      return state;
  }
}

export const ZapposForm = props => {
  const {
    fetchAdPreferences,
    setAdPreferences,
    slotDetails,
    optOutPreference
  } = props;

  const { type } = slotDetails;

  const [{ error, success }, dispatch] = useReducer(reducer, initialState);
  const [prefValue, setPrefValue] = useState(null);

  useEffect(() => {
    switch (type) {
      case 'AdvertisingPreferences':
        fetchAdPreferences();
    }
  }, [fetchAdPreferences, type]);

  useEffect(() => {
    setPrefValue(optOutPreference);
  }, [optOutPreference]);

  const {
    controls,
    heading,
    successMessage,
    failureMessage,
    action,
    method = 'post'
  } = slotDetails;

  const onSubmit = e => {
    e.preventDefault();
    dispatch(RESET);

    if (type === 'AdvertisingPreferences' && (typeof prefValue === 'string' || typeof prefValue === 'boolean')) {
      // Ads preferences specific form call
      setAdPreferences(prefValue)
        .then(res => (res?.ads?.success ? dispatch(SUCCESS) : dispatch(ERROR)))
        .catch(() => dispatch(ERROR));
    } else {
      // General all-purpose form POST
      const fetcher = timedFetch(`ZapposForm-${action}`);
      const body = JSON.stringify(formInputsToObject(e.target));
      fetcher(action, { method, body })
        .then(fetchErrorMiddleware)
        .then(() => dispatch(SUCCESS))
        .catch(err => {
          dispatch(ERROR);
          trackError('ERROR', `Zappos Form: ${action}`, err);
        });
    }
  };

  return (
    <div className={css.container}>
      <h2>{ heading }</h2>

      {!error && success &&
        <AriaLiveTee>
          <p className={css.success}>{successMessage}</p>
        </AriaLiveTee>
      }

      {error &&
        <AriaLiveTee>
          <p className={css.failure}>{failureMessage}</p>
        </AriaLiveTee>
      }

      { /* our custom eslint rule doesnt accept method as a prop */
        /* eslint-disable-next-line marty/form-has-method */ }
      <form onSubmit={onSubmit} method={method} action={action}>
        {controls.map((option, index) =>
          <FormElement
            {...option}
            key={option.label + option.name}
            index={index}
            setPrefValue={setPrefValue}
            optOutPreference={optOutPreference}/>
        )}
      </form>
    </div>
  );
};

const mapStateToProps = state => {
  const { cookies } = state;

  const adPreferenceCookie = cookies[AD_PREFERENCE_COOKIE];
  const adPreferences = adPreferenceCookie ? JSON.parse(adPreferenceCookie) : null;
  const { optOutPreference } = adPreferences || {};

  return {
    optOutPreference
  };
};

const mapDispatchToProps = {
  fetchAdPreferences,
  setAdPreferences
};

const ConnectedZapposForm = connect(mapStateToProps, mapDispatchToProps)(ZapposForm);
export default withErrorBoundary('ZapposForm', ConnectedZapposForm);
